import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { IPredisposition, IPredispositionsFilters, IRisksFilters } from "@gloria-public/home/domain/interfaces/home.interface";
import { HomeService } from "@gloria-public/home/domain/services/home.service";
import { IRiskTemplateResponse } from "@gloria-public/risk/domain/interfaces/risk.interface";
import { TranslateService } from "@ngx-translate/core";
import { BackendTableDataSource } from "@shared/utils/backend-table.datasource";
import { Subject, combineLatest, distinctUntilChanged, of, startWith, switchMap, takeUntil } from "rxjs";

@Component({
  selector: "app-home-page",
  templateUrl: "./home-page.component.html",
  styleUrls: ["./home-page.component.scss"],
})
export class HomePageComponent implements OnInit, OnDestroy {
  public predispositionsDataSource!: BackendTableDataSource<IPredisposition>;
  public predispositionsFilters: IPredispositionsFilters = { lang: this.translateService.currentLang, page: 1, pageSize: 10 };

  public notOrderedRisksDataSource!: BackendTableDataSource<IRiskTemplateResponse>;
  public notOrderedRisksFilters: IRisksFilters = { lang: "en", page: 1, pageSize: 10 };

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private homeService: HomeService,
    private translateService: TranslateService,
    public snackBar: MatSnackBar
  ) {
    this.predispositionsDataSource = new BackendTableDataSource(
      (p) =>
        combineLatest([
          of(p),
          this.translateService.onLangChange.pipe(
            startWith({ lang: this.translateService.currentLang }),
            distinctUntilChanged((a, b) => a.lang === b.lang),
            takeUntil(this.onDestroy$)
          ),
        ]).pipe(
          switchMap(([params, event]) => this.homeService.getPredispositions({ ...params, lang: event.lang })),
          takeUntil(this.onDestroy$)
        ),
      () => this.homeService.getPredispositionsCount() //GLOR-179 tags to add later on
    );

    this.notOrderedRisksDataSource = new BackendTableDataSource(
      (p) =>
        combineLatest([
          of(p),
          this.translateService.onLangChange.pipe(
            startWith({ lang: this.translateService.currentLang }),
            distinctUntilChanged((a, b) => a.lang === b.lang),
            takeUntil(this.onDestroy$)
          ),
        ]).pipe(
          switchMap(([params, event]) => this.homeService.getNotOrderedRisks({ ...params, lang: event.lang })),
          takeUntil(this.onDestroy$)
        ),
      () => this.homeService.getNotOrderedRisksCount()
    );
  }

  /**
   * Loads the datasources.
   */
  ngOnInit(): void {
    this.loadDataSources();
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Loads all backend datasources using current filters
   */
  private loadDataSources(): void {
    this.predispositionsDataSource.loadData({
      lang: this.predispositionsFilters.lang ?? "en",
      page: this.predispositionsFilters.page ?? 1,
      pageSize: this.predispositionsFilters.pageSize ?? 10,
    });
    this.notOrderedRisksDataSource.loadData({
      page: this.notOrderedRisksFilters.page ?? 1,
      pageSize: this.notOrderedRisksFilters.pageSize ?? 10,
    });
  }
}
